import type { IntegrationProvider } from '@brew/integration-contracts'
import type { IntegrationsTranslator } from '../integrations-translator'

export type { IntegrationProvider }

export type IntegrationFormSettings = {
  apiKey: string
  secretKey?: string
  region?: 'us' | 'eu'
  instance?: string
  appGroupId?: string
  appId?: string
  domain?: string
  stytchProject?: 'consumer' | 'b2b'
  webhookUrl?: string
  connectionId?: string
  revenueCatVerificationMode?: 'hmac' | 'legacy_authorization'
}

/**
 * The "shape" of an integration's connection flow. Drives:
 *  - the type chip on the integration card ("OAuth" vs "API key")
 *  - the form/UX inside the connection dialog
 *  - the runtime branching in `useIntegration` (token-based vs key-based)
 */
export type IntegrationType =
  | 'oauth'
  | 'api_key'
  | 'dual_api_key'
  | 'webhook_signing_secret'
  | 'shared_secret'
  | 'form_webhook'

export type IntegrationCatalogAvailability = 'available' | 'coming_soon'

type IntegrationEditableStepInput = {
  /**
   * - `text`/`password`: standard editable inputs.
   * - `select`: single-select dropdown driven by `options`.
   * - `copy`: read-only display of the current settings value with a
   *   copy-to-clipboard button. Used for surfacing server-issued
   *   values (e.g. the Clerk per-org webhook URL) inside a tutorial
   *   step. The value is read from `settings[input.key]`; when empty
   *   the placeholder text is rendered.
   * - `code`: read-only multi-line code block with a copy button.
   *   Static content is supplied via the `value` field; tokens of
   *   the form `{{webhookUrl}}` are substituted from `settings` at
   *   render time so the operator sees their actual minted URL
   *   inside the SQL/script. Use for SQL setup blocks, shell
   *   commands, and any inline source the operator needs to copy
   *   into their provider dashboard verbatim.
   */
  type: 'text' | 'password' | 'select' | 'copy'
  key: keyof IntegrationFormSettings
  placeholder?: string
  options?: Array<{ value: string; label: string }>
  /**
   * For `type: 'code'` only — the static content rendered inside
   * the code block. Supports `{{webhookUrl}}` substitution.
   */
  value?: never
  /**
   * For `type: 'code'` only — optional language label shown above
   * the block (e.g. `'sql'`, `'bash'`). Display-only; no syntax
   * highlighting is applied.
   */
  language?: never
}

type IntegrationCodeStepInput = {
  type: 'code'
  value: string
  language?: string
  key?: never
  placeholder?: never
  options?: never
}

type IntegrationStepInput =
  | IntegrationEditableStepInput
  | IntegrationCodeStepInput

interface IntegrationStep {
  label: string
  description: string
  input?: IntegrationStepInput
}

export interface IntegrationMeta {
  id: IntegrationProvider
  name: string
  /** Public domain used only as a fallback when Brew CDN assets fail. */
  brandDomain: string
  /**
   * Optional logo tint override. `'black'` forces the integration logo to render
   * as a black silhouette (white in dark mode) — useful when the brand color is
   * too saturated next to the rest of the integrations grid.
   */
  logoTone?: 'black'
  /**
   * Optional logo background override. `'black'` paints a black rectangle
   * behind the logo so brands whose marks are white-on-dark (e.g. Iterable)
   * stay legible on the light-mode card surface.
   */
  logoBackground?: 'black'
  description: string
  shortDescription: string
  author: string
  availability: IntegrationCatalogAvailability
  createdByBrew: boolean
  lastEditedBy: string
  lastEditedAt: string
  dataDirection: 'data_in' | 'data_out'
  /**
   * Single source of truth for the integration's connection flow shape.
   * Replaces the old `isOAuth` boolean and the runtime `Set`s in
   * `use-integration.ts`.
   */
  integrationType: IntegrationType
  tutorialVideoUrl?: string
  tutorialVideoThumbnail?: string
  /**
   * Public docs URL on docs.brew.new. Used as a fallback to the video tutorial
   * side panel when an integration has no recorded walkthrough.
   */
  tutorialDocsUrl?: string
  tutorialSteps?: Array<IntegrationStep>
  /**
   * How to send the first signed event that activates a connection waiting
   * for verification. Shown in the Connect toast and the waiting summary in
   * place of the generic "send a signed test event" line.
   */
  testEventHint?: string
  /**
   * Only set when `integrationType === 'oauth'`. Identifies which Brew OAuth
   * subsystem handles the handshake.
   */
  oAuthProvider?:
    | 'figma'
    | 'slack'
    | 'hubspot'
    | 'stripe'
    | 'klaviyo'
    | 'mailchimp'
}

/** Provider metadata before the canonical provider key is attached. */
export type IntegrationDefinition = Omit<IntegrationMeta, 'id'>

export type IntegrationAvailability = 'available' | 'coming_soon' | 'connected'

/** Each connection type's key in the `integrations` catalog (`types`). */
export const INTEGRATION_TYPE_KEY = {
  oauth: 'oauth',
  api_key: 'apiKey',
  dual_api_key: 'apiKeyPair',
  webhook_signing_secret: 'webhookSecret',
  shared_secret: 'sharedSecret',
  form_webhook: 'formWebhook',
} as const satisfies Record<IntegrationType, string>

/** How an integration connects, in the reader's language. */
export function integrationTypeLabel(
  t: IntegrationsTranslator,
  type: IntegrationType
): string {
  return t(`types.${INTEGRATION_TYPE_KEY[type]}`)
}

/**
 * How an integration connects, in English, for the structured data, which
 * names it the same in every language. A reader (and the Markdown twin)
 * sees `integrationTypeLabel`, which the English catalog words the same way.
 */
export function getIntegrationTypeLabel(type: IntegrationType): string {
  if (type === 'form_webhook') {
    return 'Form webhook'
  }
  if (type === 'oauth') {
    return 'OAuth'
  }
  if (type === 'dual_api_key') {
    return 'API key pair'
  }
  if (type === 'webhook_signing_secret') {
    return 'Webhook secret'
  }
  if (type === 'shared_secret') {
    return 'Shared secret'
  }
  return 'API key'
}

export function getIntegrationAvailability(
  meta: Pick<IntegrationMeta, 'availability'>,
  isConnected: boolean
): IntegrationAvailability {
  if (meta.availability === 'coming_soon') {
    return 'coming_soon'
  }
  if (isConnected) {
    return 'connected'
  }
  return 'available'
}

/** Each availability's key in the `integrations` catalog (`status`). */
const AVAILABILITY_KEY = {
  connected: 'connected',
  coming_soon: 'comingSoon',
  available: 'available',
} as const satisfies Record<IntegrationAvailability, string>

/** An integration card's availability, in the reader's language. */
export function getAvailabilityLabel(
  t: IntegrationsTranslator,
  availability: IntegrationAvailability
): string {
  return t(`status.${AVAILABILITY_KEY[availability]}`)
}
